import type { SxProps, Theme } from '@mui/material/styles';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { mergeSx } from 'ui/merge-sx';

import Divider from 'components/shared/others/divider/divider';

import {
  BAR_MAX_HEIGHT,
  VALUE_LABEL_SPACE,
} from 'containers/user/goals/goals-monitoring/monitoring-chart/components/constants';
import {
  formatNumber,
  renderChangeValue,
  pluralizeMetric,
} from 'containers/user/goals/goals-monitoring/monitoring-chart/components/metric-block.helpers';

const BAR_SX: SxProps<Theme> = {
  transitionProperty: 'all',
  transitionDuration: '700ms',
  transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
};

const PRIMARY_BAR_COLOR_SX: SxProps<Theme> = { bgcolor: 'tokens.blue.700' };
const SECONDARY_BAR_COLOR_SX: SxProps<Theme> = { bgcolor: 'tokens.blue.800' };

interface MetricBlockProps {
  metricKey: string;
  unit?: string;
  primary?: number | null;
  secondary?: number | null;
  primaryLabel?: string | number;
  secondaryLabel?: string | number;
  change?: number | string | null;
  cohortChange?: number | string | null;
  isRate?: boolean;
  cohortName?: string;
}

interface ColumnArgs {
  value: number | null | undefined;
  height: number;
  colorSx: SxProps<Theme>;
  label: string | number | undefined;
}

const MetricBlock = ({
  metricKey,
  unit,
  primary,
  secondary,
  primaryLabel,
  secondaryLabel,
  change,
  cohortChange,
  isRate,
  cohortName = 'Cohort',
}: MetricBlockProps): React.JSX.Element => {
  const primaryVal = primary;
  const secondaryVal = secondary;

  const max = Math.max(primaryVal || 0, secondaryVal || 0, 1);
  const barHeight1 = ((primaryVal as number) / max) * BAR_MAX_HEIGHT;
  const barHeight2 = ((secondaryVal as number) / max) * BAR_MAX_HEIGHT;

  const renderColumn = ({
    value,
    height,
    colorSx,
    label,
  }: ColumnArgs): React.JSX.Element => (
    <Stack
      sx={{
        textAlign: 'center',
        justifyContent: 'flex-end',
        px: 1.25,
        width: '100%',
      }}
    >
      <Box
        style={{ height: `${BAR_MAX_HEIGHT + VALUE_LABEL_SPACE}px` }}
        sx={{ position: 'relative', width: '100%', overflow: 'hidden' }}
      >
        <Box sx={mergeSx(BAR_SX, colorSx)} style={{ height: `${height}px` }} />
        <span
          className="absolute left-1/2 -translate-x-1/2 text-xs font-semibold text-neutral-950"
          style={{ bottom: height + 4 }}
        >
          {formatNumber(value, unit)}
          {unit}
        </span>
      </Box>
      <p className="text-xs mt-1 text-gray-500">{label}</p>
    </Stack>
  );

  const renderChangeRow = (
    label: string,
    value: number | string | null | undefined
  ): React.JSX.Element => (
    <Stack
      direction="row"
      sx={{
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ color: 'tokens.gray.500', flex: '1 1 0%' }} title={label}>
        {label}
      </Box>
      <Box
        sx={{
          fontWeight: 600,
          color: 'tokens.gray.700',
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {renderChangeValue(value)}%
      </Box>
    </Stack>
  );

  const rawMetricType = metricKey.replace(/([A-Z])/g, ' $1').trim();

  const metricType = pluralizeMetric(rawMetricType);

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          textAlign: 'center',
          fontWeight: 600,
          color: 'tokens.gray.700',
          mb: 1,
          textTransform: 'capitalize',
          px: 1.25,
        }}
      >
        {metricType}
      </Box>

      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}
      >
        {renderColumn({
          value: primaryVal,
          height: barHeight1,
          colorSx: PRIMARY_BAR_COLOR_SX,
          label: primaryLabel,
        })}
        {renderColumn({
          value: secondaryVal,
          height: barHeight2,
          colorSx: SECONDARY_BAR_COLOR_SX,
          label: secondaryLabel,
        })}
      </Stack>

      <Divider customClass="my-2" />
      <Stack
        sx={{
          mt: 2,
          fontSize: '0.75rem',
          lineHeight: '1rem',
          textAlign: 'left',
          color: 'tokens.gray.500',
          fontWeight: 400,
          gap: 0.5,
          px: 1.25,
        }}
      >
        {renderChangeRow(`My School ${isRate ? 'rate' : 'change'}`, change)}
        {renderChangeRow(
          `${cohortName} ${isRate ? 'rate' : 'change'}`,
          cohortChange
        )}
      </Stack>
    </Box>
  );
};

export default MetricBlock;
